import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    ClientInfo,
    CreateClientInfoRequest,
    CreateClientInfoResponse,
    UpdateClientInfoRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/api/clientInfo.api.ts';
import { updateApiClientInfo } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/api/clientInfo.api.ts';
import {
    createApiClientInfo,
    getApiClientInfo,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/api/clientInfo.api.ts';
import { CLIENT_INFO_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/constants.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import { extractError } from '@utils/slice.ts';

export interface ClientInfoError {
    message?: string;
    [k: string]: any;
}

export interface ClientInfoState {
    error: ClientInfoError | null;
    clientInfo: ClientInfo | null;
    clientInfoId: number | null; // we use this id thought steps and get client info data in step 1
    clientName: string | null; // use for Tenant Bar view

    isCreateClientInfoPending: boolean;
    isGetClientInfoPending: boolean;
    isUpdateClientInfoPending: boolean;
}

const initialState: ClientInfoState = {
    error: null,
    clientInfo: null,
    clientInfoId: null,
    clientName: null,

    isCreateClientInfoPending: false,
    isGetClientInfoPending: false,
    isUpdateClientInfoPending: false,
};

export const createClientInfo = createAsyncThunk<
    CreateClientInfoResponse,
    CreateClientInfoRequest,
    { rejectValue: ClientInfoError }
>('adminCenterClientInfo/createApiClientInfo', (payload, { rejectWithValue, dispatch }) =>
    createApiClientInfo(payload)
        .then((res) => {
            dispatch(setPublishedSteps(CLIENT_INFO_STEP_TITLE));

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const getClientInfo = createAsyncThunk<ClientInfo, number | string, { rejectValue: ClientInfoError }>(
    'adminCenterClientInfo/getApiClientInfo',
    (clientId, { rejectWithValue }) =>
        getApiClientInfo(clientId)
            .then((res) => {
                return res;
            })
            .catch((err) => rejectWithValue(err)),
);

export const updateClientInfo = createAsyncThunk<
    ClientInfo,
    { clientId: number | string; payload: UpdateClientInfoRequest },
    { rejectValue: ClientInfoError }
>('adminCenterClientInfo/updateApiClientInfo', ({ clientId, payload }, { rejectWithValue }) =>
    updateApiClientInfo(clientId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const clientInfoSlice = createSlice({
    name: 'adminCenterClientInfo',
    initialState,
    reducers: {
        clearClientInfo(state) {
            state.clientInfo = null;
            state.clientInfoId = null;
            state.clientName = '';
            state.error = null;
        },
        setClientEditContext(state, action: PayloadAction<{ id: number; name: string }>) {
            const { id, name } = action.payload;
            state.clientInfoId = id;
            state.clientName = name;
        },
        setClientInfoId(state, action: PayloadAction<number>) {
            state.clientInfoId = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createClientInfo.pending, (state) => {
            state.isCreateClientInfoPending = true;
            state.error = null;
        });
        builder.addCase(createClientInfo.fulfilled, (state, action) => {
            state.isCreateClientInfoPending = false;
            state.clientInfoId = action.payload.id;
            state.clientName = action.payload.name;
        });
        builder.addCase(createClientInfo.rejected, (state, action) => {
            state.isCreateClientInfoPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getClientInfo.pending, (state) => {
            state.isGetClientInfoPending = true;
            state.error = null;
        });
        builder.addCase(getClientInfo.fulfilled, (state, action) => {
            state.isGetClientInfoPending = false;
            state.clientInfo = action.payload;
        });
        builder.addCase(getClientInfo.rejected, (state, action) => {
            state.isGetClientInfoPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateClientInfo.pending, (state) => {
            state.isUpdateClientInfoPending = true;
            state.error = null;
        });
        builder.addCase(updateClientInfo.fulfilled, (state, action) => {
            state.isUpdateClientInfoPending = false;
            state.clientInfo = action.payload;
            state.clientInfoId = action.payload.id;
            state.clientName = action.payload.name;
        });
        builder.addCase(updateClientInfo.rejected, (state, action) => {
            state.isUpdateClientInfoPending = false;
            state.error = extractError(action);
        });
    },
});

export const { clearClientInfo, setClientEditContext, setClientInfoId } = clientInfoSlice.actions;
export default clientInfoSlice.reducer;
